import { InfoIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Skeleton,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { Appearance, loadStripe } from '@stripe/stripe-js'
import { TestimonialCarousel } from 'components/Carousel/TestimonialCarousel'
import { useBreakpointContext } from 'context/Breakpoint'
import {
  PaymentPlan,
  useOnboardingContext,
} from 'context/OnboardingContext/OnboardingProvider'
import { Formik, Form as FormikForm } from 'formik'
import withAccount, { WithAccountProps } from 'hoc/withAccount'
import usePayment from 'hooks/usePayment/usePayment'
import usePaymentPlan from 'hooks/usePayment/usePaymentPlan'
import useUser from 'hooks/useUser/useUser'
import { useEffect, useState } from 'react'
import './styles.scss'
// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
)

interface PaymentFormProps {
  onError: (error: string) => void
  handleBook: () => void
  previousScreen: () => void
}

interface PaymentBoxProps {
  handleBook: () => void
  previousScreen: () => void
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  onError,
  handleBook,
  previousScreen,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const { createPaymentIntent } = usePayment()
  const { selectedPlan, setSelectedPlan } = useOnboardingContext()
  const { initializeInsuranceTimeline } = useUser()
  const { fetchPlan } = usePaymentPlan()
  const toast = useToast()
  const { isMobile } = useBreakpointContext()

  const fetch0InsurancePlan = async () => {
    const data = await fetchPlan(
      process.env.REACT_APP_0_DOLLAR_INSURANCE_PRICE_ID ?? ''
    )

    if (data) {
      setSelectedPlan(data)
    }
  }

  useEffect(() => {
    fetch0InsurancePlan()
  }, [])

  const handleSubmit = async (values: { acceptedTerms: boolean }) => {
    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)

    try {
      // 1. Confirm the setup (creates payment method)
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-complete`,
        },
        redirect: 'if_required',
      })

      if (setupError) {
        throw new Error(setupError.message)
      }

      if (!setupIntent?.payment_method) {
        throw new Error('No payment method was created')
      }

      // 2. Create subscription with the new payment method
      const response = await createPaymentIntent(selectedPlan as PaymentPlan)

      await initializeInsuranceTimeline()
      handleBook()
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred'
      toast({
        title: 'Payment Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      })
      onError(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Formik
      initialValues={{
        acceptedTerms: false,
        acceptedCancellationPolicy: false,
      }}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <FormikForm>
          <>
            <Box
              p={[2, 3, 4]}
              bg="white"
              borderRadius="md"
              width={['100%', '100%', '3xl']}
            >
              <VStack spacing={4} align="stretch">
                <Box bg={'var(--blue-lightest)'} p={4} borderRadius="md">
                  <HStack alignItems="start" spacing={3}>
                    <InfoIcon mt={2} />
                    <Box>
                      <Text fontSize="lg" fontWeight="bold" mb={1}>
                        You will not be charged today
                      </Text>
                      <Text fontSize="sm" mb={0}>
                        We will only verify your payment method and keep it on
                        file in case of copays, co-insurance, or deductibles
                        assigned by your insurance.
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                <Box>
                  <PaymentElement
                    options={{
                      layout: 'tabs',
                      wallets: {
                        applePay: 'auto',
                        googlePay: 'auto',
                      },
                    }}
                  />
                </Box>

                <Checkbox
                  isChecked={values.acceptedTerms}
                  onChange={(e) =>
                    setFieldValue('acceptedTerms', e.target.checked)
                  }
                  size="sm"
                >
                  By checking this box you acknowledge that your payment
                  information will be kept on file for outstanding charges such
                  as copays or coinsurance.
                </Checkbox>
                <Checkbox
                  isChecked={values.acceptedCancellationPolicy}
                  onChange={(e) =>
                    setFieldValue(
                      'acceptedCancellationPolicy',
                      e.target.checked
                    )
                  }
                  size="sm"
                >
                  Please confirm you understand and agree to our Cancellation
                  and No Show Policy (see below).
                </Checkbox>
              </VStack>
            </Box>
            <Button
              type="submit"
              backgroundColor="var(--blue-dark)"
              color="white"
              _hover={{
                backgroundColor: 'var(--blue-dark-hover)',
                color: 'white',
              }}
              isLoading={isProcessing}
              isDisabled={
                !stripe ||
                isProcessing ||
                !values.acceptedTerms ||
                !values.acceptedCancellationPolicy
              }
              width={['45%', '30%', '15%']}
              mt={4}
              mb={4}
            >
              Book
            </Button>

            {isMobile && (
              <>
                <Skeleton
                  isLoaded={true}
                  fontFamily={'Instrument Serif'}
                  fontSize="28px"
                >
                  What patients <i>love</i> about Allara
                </Skeleton>
                <TestimonialCarousel />
              </>
            )}

            <Box
              color="var(--gray-dark)"
              width={['100%', '100%', '3xl']}
              px={[2, 3, 0]}
            >
              <Text
                fontSize="xs"
                textTransform="uppercase"
                mb={0}
                color="var(--gray-dark)"
              >
                Billing Info:
              </Text>
              <Text fontSize="xs">
                You will be responsible for any copays, coinsurance, or
                deductibles assigned by your insurance, if applicable. We will
                charge the credit or debit card on file once your insurance
                processes the claim, typically after your appointment. If you
                are out of network, you will only be billed $120 for your
                initial medical visit. For nutrition services, since not all
                insurance plans provide coverage, we offer a self-pay rate of
                $75 for both the initial and ongoing nutrition visits if your
                insurance does not cover them. We will attempt to bill your
                insurance first before charging you. Please note: if you have an
                outstanding balance, future appointments may be cancelled or
                blocked from booking until the balance is resolved.
              </Text>
            </Box>
          </>
        </FormikForm>
      )}
    </Formik>
  )
}

// Create a separate component for the wrapped form
const PaymentBoxContent: React.FC<PaymentBoxProps> = ({
  handleBook,
  previousScreen,
}) => {
  const { createSetupIntent } = usePayment()
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  useEffect(() => {
    const initializePayment = async () => {
      try {
        const { clientSecret } = await createSetupIntent()
        setClientSecret(clientSecret)
      } catch (error) {
        console.error('Error initializing setup:', error)
      }
    }

    if (!clientSecret) {
      // Only call if we don't have a clientSecret
      initializePayment()
    }
  }, [])

  if (!clientSecret) {
    return <div>Loading...</div>
  }

  const appearance: Appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: 'var(--blue-dark)',
      colorBackground: '#ffffff',
      colorText: '#30313d',
    },
  }

  const options = {
    clientSecret,
    appearance,
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm
        onError={(error) => console.error('Payment error:', error)}
        handleBook={handleBook}
        previousScreen={previousScreen}
      />
    </Elements>
  )
}

// Main component no longer uses Stripe hooks directly
const PaymentBox: React.FC<WithAccountProps & PaymentBoxProps> = ({
  handleBook,
  previousScreen,
}) => {
  return (
    <PaymentBoxContent
      handleBook={handleBook}
      previousScreen={previousScreen}
    />
  )
}

export default withAccount(PaymentBox)
